import { calculateNights } from 'entities/kyrgyzstan/lib'
import type { DetailSearchParams } from 'entities/kyrgyzstan/model'
import { DefaultGuests } from 'entities/kyrgyzstan/model/search/search-store'

import type {
  CreateOrderRequest,
  PaymentStatus,
  PersonalData,
} from 'shared/api/kyrgyzstan/orders'
import { formatDateForApi } from 'shared/lib/formatDate'
import { preparePhoneNumberForRequest } from 'shared/lib/prepare-phone-number'

import type { BookingFormValues } from '../model'

interface GetBookingSubmitParamsOptions {
  formData: BookingFormValues
  realtyId?: string
  detailSearchParams?: DetailSearchParams | null
  selectedPayment?: PaymentStatus
  ratehawkBookHash?: string | null
}

export interface BookingSubmitParams {
  personalDataPayload: PersonalData
  orderPayload: CreateOrderRequest
}

interface GetOrderPayloadParams {
  realtyId: string
  personalDataPayload: PersonalData
  startDate: string
  endDate: string
  nights: number
  visitorsCount: number
  childrenAges: number[]
  selectedPayment: PaymentStatus
  ratehawkBookHash?: string | null
}

const getPersonalDataPayload = (formData: BookingFormValues): PersonalData => {
  const middleName = formData.middleName?.trim()

  return {
    first_name: formData.firstName.trim(),
    last_name: formData.lastName.trim(),
    ...(middleName ? { middle_name: middleName } : {}),
    email: formData.email.trim(),
    phone: preparePhoneNumberForRequest(formData.phone) || '',
  }
}

const getOrderPayload = ({
  realtyId,
  personalDataPayload,
  startDate,
  endDate,
  nights,
  visitorsCount,
  childrenAges,
  selectedPayment,
  ratehawkBookHash,
}: GetOrderPayloadParams): CreateOrderRequest => ({
  realty_object_id: realtyId,
  personal_data: personalDataPayload,
  start_date: formatDateForApi(startDate),
  end_date: formatDateForApi(endDate),
  nights,
  visitors_count: visitorsCount,
  ...(childrenAges.length ? { children_ages: childrenAges } : {}),
  ...(ratehawkBookHash ? { ratehawk_book_hash: ratehawkBookHash } : {}),
  payment_status: selectedPayment,
})

export const getBookingSubmitParams = ({
  formData,
  realtyId,
  detailSearchParams,
  selectedPayment,
  ratehawkBookHash,
}: GetBookingSubmitParamsOptions): BookingSubmitParams | null => {
  const startDate = detailSearchParams?.start_date
  const endDate = detailSearchParams?.end_date
  const visitorsCount =
    detailSearchParams?.visitors_count ?? DefaultGuests.adults
  const childrenAges = detailSearchParams?.children_ages ?? []
  const nights = calculateNights(startDate, endDate)

  if (
    !realtyId ||
    !selectedPayment ||
    !startDate ||
    !endDate ||
    !nights ||
    nights <= 0
  ) {
    return null
  }

  const personalDataPayload = getPersonalDataPayload(formData)
  const orderPayload = getOrderPayload({
    realtyId,
    personalDataPayload,
    startDate,
    endDate,
    nights,
    visitorsCount,
    childrenAges,
    selectedPayment,
    ratehawkBookHash,
  })

  return {
    personalDataPayload,
    orderPayload,
  }
}
